import { trackClientError } from "./gameAnalytics";
import type { ClientErrorSource } from "./events";

/**
 * PR28: minimal client-side error observability. Before this module, an
 * uncaught exception or unhandled promise rejection anywhere in the app
 * was invisible to the team - it reached only the browser's own console,
 * never PostHog, never any log. This is deliberately small: two global
 * listeners, wired once, reporting a single `client_error` event through
 * the existing analytics pipeline (`trackEvent()`/`capture()` in
 * `track.ts`/`posthogClient.ts`) - no new vendor, no Sentry, no session
 * replay, no large context framework.
 *
 * `capture()` already no-ops when analytics isn't enabled (non-prod,
 * missing key, wrong host, or `window` not existing at all - see
 * `posthogClient.ts`) and already never throws, so this module doesn't
 * need to re-implement either guarantee - it only needs to normalize an
 * arbitrary, untrusted error/rejection value into a small, safe payload
 * before handing it to `trackClientError()`.
 */

const MAX_MESSAGE_LENGTH = 300;
const MAX_STACK_LENGTH = 500;
/**
 * Hard ceiling on `client_error` events per page load. The dedupe below
 * only suppresses an exact repeat of the immediately-preceding report, so
 * two different errors alternating every frame would otherwise stream
 * events for as long as the page stays open. A healthy page reports 0-2
 * errors; 20 still keeps every realistic real-world error visible while
 * guaranteeing a broken page can never produce unbounded analytics volume.
 */
const MAX_REPORTS_PER_PAGE_LOAD = 20;

let installed = false;
/** A crude same-signature dedupe (not time-windowed) - a pathological error loop (e.g. a render-loop exception firing every frame) must never turn into an equally pathological stream of analytics events. Deliberately simple: only suppresses an EXACT repeat of the immediately-preceding report, never a persistent cache. */
let lastSignature: string | null = null;
/** How many `client_error` events this page load has actually sent - never persisted, so a reload always starts from 0. See `MAX_REPORTS_PER_PAGE_LOAD`. */
let reportedCount = 0;

/**
 * Strips anything that looks like a URL query string or hash fragment,
 * then truncates. `sanitizeAnalyticsProperties` (`posthogClient.ts`)
 * already rewrites the one specific `/c/<id>` Beat My Score token shape
 * everywhere in the payload as a second, independent layer - this is the
 * general-purpose net for everything else an error message/stack could
 * incidentally contain (e.g. a failed request URL logged with its own
 * query string).
 */
function sanitizeText(text: string, maxLength: number): string {
  const withoutQueryOrHash = text.replace(/([?#])\S*/g, "$1");
  return withoutQueryOrHash.length > maxLength ? `${withoutQueryOrHash.slice(0, maxLength)}…` : withoutQueryOrHash;
}

/**
 * Never serializes an arbitrary rejected value - a plain object could
 * hold anything (a Supabase error body, an event object, application
 * state), and this module has no way to know whether any of it is
 * sensitive. Only a real `Error`'s own `message`/`stack`, or a plain
 * string, are ever used verbatim (then sanitized/truncated); anything
 * else becomes a fixed, generic label naming only its JS type.
 */
export function normalizeErrorReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return {
      message: sanitizeText(reason.message || reason.name || "Error", MAX_MESSAGE_LENGTH),
      stack: reason.stack ? sanitizeText(reason.stack, MAX_STACK_LENGTH) : undefined,
    };
  }
  if (typeof reason === "string") {
    return { message: sanitizeText(reason, MAX_MESSAGE_LENGTH) };
  }
  if (reason === null || reason === undefined) {
    return { message: `Non-error value rejected (${String(reason)})` };
  }
  return { message: `Non-error value rejected (${typeof reason})` };
}

function report(source: ClientErrorSource, reason: unknown): void {
  try {
    const { message, stack } = normalizeErrorReason(reason);
    const signature = `${source}:${message}`;
    if (signature === lastSignature) return;
    lastSignature = signature;
    if (reportedCount >= MAX_REPORTS_PER_PAGE_LOAD) return;
    reportedCount += 1;

    trackClientError({
      source,
      message,
      stack,
      path: typeof window !== "undefined" ? window.location.pathname : "",
    });
  } catch {
    // The error handler itself must never throw, recursively or otherwise.
  }
}

function handleWindowError(event: ErrorEvent): void {
  report("window_error", event.error ?? event.message);
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  report("unhandled_rejection", event.reason);
}

/**
 * Installs the app's ONLY global uncaught-error/unhandled-rejection
 * listeners, wired once from `main.ts`. Idempotent (a second call is a
 * no-op) - the same "install a global listener exactly once" guarantee
 * `posthogClient.ts`'s `didInit` and `online/bootstrap.ts`'s `started`
 * flag already provide for their own listeners, so a stray second call
 * (e.g. a future hot-reload path) can never register a duplicate pair.
 */
export function initErrorReporting(): void {
  if (installed) return;
  installed = true;
  if (typeof window === "undefined") return;

  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
}

/** Test-only: resets module state between tests, same precedent as `posthogClient.ts`'s `__resetAnalyticsClientForTests`. */
export function __resetErrorReportingForTests(): void {
  installed = false;
  lastSignature = null;
  reportedCount = 0;
  if (typeof window !== "undefined") {
    window.removeEventListener("error", handleWindowError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }
}
